/* All prices, quantities and discounts are rendered by Shopify after a cart change. */
import { replaceCartVariant } from '@maya/cart-variant';
import { deliveryEstimate } from '@maya/cart-delivery';
import { refreshAffirmMessaging, updateAffirmAmount } from '@maya/cart-affirm';
const source = 'maya-cart-page';
const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const cartRoot = () => document.querySelector('mb-cart-page');
const cartRegions = () => [...document.querySelectorAll('[data-mb-cart-section]')];
const jsonURL = (url) => `${url.replace(/\.js$/, '')}.js`;

function updateProductImage(container, option, sizes = '(min-width: 1024px) 30vw, 100vw') {
  let image = container.querySelector('img');
  if (option?.dataset.image) {
    if (!image) { image = document.createElement('img'); image.loading = 'lazy'; container.append(image); }
    image.sizes = sizes;
    image.srcset = option.dataset.imageSrcset || '';
    image.src = option.dataset.image;
    image.alt = option.dataset.imageAlt || '';
    image.width = Number(option.dataset.imageWidth) || 900;
    image.height = Number(option.dataset.imageHeight) || 900;
    image.hidden = false;
  } else if (image) {
    image.hidden = true;
    image.removeAttribute('srcset');
    image.removeAttribute('src');
  }
}

async function requestJSON(url, options = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 15000);
  try {
    const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal });
    let data;
    try { data = await response.json(); }
    catch (_) { throw new Error('We could not read the cart response. Please check your latest cart before trying again.'); }
    if (!response.ok) throw new Error(typeof data.description === 'string' ? data.description : 'We could not update your cart. Please try again.');
    return data;
  } finally {
    clearTimeout(timer);
  }
}

class MayaCartController {
  busy = false;
  refreshAgain = false;
  disabledControls = [];
  openers = new WeakMap();

  constructor() {
    this.stickyCheckoutObserver = new IntersectionObserver(() => this.scheduleStickyVisibility(), { threshold: [0, 1] });
    this.stickySizeObserver = new ResizeObserver(() => this.scheduleStickyVisibility());
    // Shared by warranty, configuration, benefits and the compact popup.
    document.addEventListener('pointerdown', () => { document.documentElement.dataset.mbCartInput = 'pointer'; }, { capture: true });
    document.addEventListener('keydown', (event) => {
      if (!event.metaKey && !event.ctrlKey && !event.altKey) document.documentElement.dataset.mbCartInput = 'keyboard';
    }, { capture: true });
    document.addEventListener('click', (event) => this.onClick(event));
    document.addEventListener('change', (event) => this.onChange(event));
    document.addEventListener('maya:cart:configuration', (event) => this.replaceVariant(event.detail.key, event.detail.variantID, event.detail.focus));
    document.addEventListener('submit', (event) => this.onSubmit(event));
    document.addEventListener('cart:update', (event) => {
      if (event.detail?.data?.source !== source) this.refresh();
    });
    window.addEventListener('pageshow', (event) => { this.resetCheckout(); if (event.persisted) this.refresh(); });
    window.addEventListener('scroll', () => this.scheduleStickyVisibility(), { passive: true });
    window.addEventListener('resize', () => this.scheduleStickyVisibility());
    document.addEventListener('shopify:section:load', () => this.enhance());
    document.addEventListener('shopify:section:reorder', () => this.enhance());
    document.addEventListener('shopify:section:unload', () => requestAnimationFrame(() => this.enhance()));
    this.enhance();
  }

  enhance() {
    cartRoot()?.setAttribute('data-enhanced', '');
    const form = cartRoot()?.querySelector('.mb-cart-layout');
    this.stickySizeObserver.disconnect();
    document.querySelectorAll('mb-cart-sticky-checkout').forEach((bar) => {
      this.stickySizeObserver.observe(bar);
      const button = bar.querySelector('[data-sticky-checkout]');
      if (form && button) {
        button.setAttribute('form', form.id);
        button.disabled = this.busy || !!document.querySelector('[data-cart-unconfirmed]');
        bar.setAttribute('data-enhanced', '');
      } else bar.removeAttribute('data-enhanced');
    });
    this.stickyCheckoutObserver.disconnect();
    const checkout = cartRoot()?.querySelector('.mb-cart-summary .mb-cart-checkout');
    if (checkout) this.stickyCheckoutObserver.observe(checkout);
    this.updateStickyVisibility();
    refreshAffirmMessaging();
    cartRoot()?.querySelectorAll('dialog:not([data-bound])').forEach((dialog) => {
      dialog.setAttribute('data-bound', '');
      dialog.addEventListener('cancel', (event) => { if (this.busy) event.preventDefault(); });
      dialog.addEventListener('close', () => {
        if (dialog.hasAttribute('data-affirm-suspended')) return;
        if (!document.querySelector('.mb-cart-dialog[open]')) document.body.classList.remove('mb-cart-modal-open');
        const opener = this.openers.get(dialog);
        if (opener?.isConnected) opener.focus({ preventScroll: true });
      });
      dialog.addEventListener('click', (event) => {
        if (this.busy || event.target !== dialog) return;
        const rect = dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
      });
    });
  }

  scheduleStickyVisibility() {
    if (this.stickyVisibilityFrame) return;
    this.stickyVisibilityFrame = requestAnimationFrame(() => {
      this.stickyVisibilityFrame = null;
      this.updateStickyVisibility();
    });
  }

  updateStickyVisibility() {
    const checkout = cartRoot()?.querySelector('.mb-cart-summary .mb-cart-checkout');
    const accessoriesVisible = [...document.querySelectorAll('mb-cart-accessories:not([hidden]) .mb-cart-accessory')].some((card) => card.getClientRects().length > 0);
    // Stay hidden after passing checkout; return when scrolling back above it.
    const checkoutReached = !!checkout && checkout.getBoundingClientRect().bottom <= window.innerHeight;
    let stickyHeight = 0;
    document.querySelectorAll('mb-cart-sticky-checkout').forEach((bar) => {
      bar.toggleAttribute('data-checkout-reached', bar.dataset.hideAtCheckout === 'true' && !accessoriesVisible && checkoutReached);
      const rect = bar.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) stickyHeight = Math.max(stickyHeight, rect.height);
    });
    document.documentElement.style.setProperty('--mb-cart-sticky-height', `${Math.ceil(stickyHeight)}px`);
  }

  sectionIDs() {
    const page = cartRegions().map((region) => region.dataset.mbCartSection);
    const drawer = [...document.querySelectorAll('cart-items-component[data-section-id], mb-cart-popup[data-section-id]')].map((element) => element.dataset.sectionId);
    return [...new Set([...page, ...drawer])];
  }

  setBusy(value) {
    this.busy = value;
    for (const region of cartRegions()) region.setAttribute('aria-busy', String(value));
    document.querySelectorAll('mb-cart-popup').forEach((popup) => popup.setAttribute('aria-busy', String(value)));
    if (value) {
      this.disabledControls = [...cartRegions(), ...document.querySelectorAll('mb-cart-popup')].flatMap((region) => [...region.querySelectorAll('button:not([data-popup-close]), input, select')]).filter((control) => !control.disabled);
      this.disabledControls.forEach((control) => { control.disabled = true; });
    } else {
      this.disabledControls.forEach((control) => { if (control.isConnected) control.disabled = false; });
      this.disabledControls = [];
      document.querySelectorAll('[data-sticky-checkout]').forEach((button) => { button.disabled = !!document.querySelector('[data-cart-unconfirmed]'); });
    }
  }

  error(message, region) {
    const element = (region?.isConnected ? region : cartRoot())?.querySelector('.mb-cart-error');
    if (!element) return;
    element.textContent = message;
    element.hidden = !message;
  }

  onClick(event) {
    const chatLink = event.target.closest('[data-cart-chat]');
    if (chatLink && !event.defaultPrevented && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
      const chat = document.querySelector('shopify-chat');
      const launcher = chat?.shadowRoot?.querySelector('button[part~="activator"]');
      // Reuse the installed Inbox launcher; the real contact URL remains the fallback.
      if (chat?.hasAttribute('open') || launcher) {
        event.preventDefault();
        if (!chat.hasAttribute('open') && launcher?.getAttribute('aria-expanded') !== 'true') launcher?.click();
      }
      return;
    }
    const opener = event.target.closest('[data-cart-dialog-open]');
    if (opener) {
      if (this.busy) return;
      const dialog = document.getElementById(opener.dataset.cartDialogOpen);
      if (!dialog) return;
      dialog.closest('mb-cart-configuration')?.reset();
      this.openers.set(dialog, opener);
      document.body.classList.add('mb-cart-modal-open');
      dialog.showModal();
      dialog.querySelector('.mb-cart-config-scroll')?.scrollTo({ top: 0, behavior: 'instant' });
      if (dialog.querySelector('[data-config-financing]')) refreshAffirmMessaging();
      return;
    }
    if (event.target.closest('[data-cart-dialog-close]')) {
      if (!this.busy) event.target.closest('dialog').close();
      return;
    }
    const upgrade = event.target.closest('[data-cart-replace]');
    if (upgrade) {
      this.replaceVariant(upgrade.closest('[data-line-key]').dataset.lineKey, upgrade.dataset.cartReplace, upgrade);
      return;
    }
    const button = event.target.closest('[data-cart-remove]');
    if (!button?.closest('mb-cart-page, mb-cart-popup') || !cartRoot()) return;
    event.preventDefault();
    if (this.busy) return;
    const row = button.closest('[data-line-key]');
    this.change(row.dataset.lineKey, 0, button);
  }

  onChange(event) {
    const input = event.target;
    if (input.matches('[data-accessory-variant]')) {
      const option = input.selectedOptions[0];
      const card = input.closest('[data-accessory]');
      card.querySelector('[data-accessory-price]').textContent = option.dataset.price;
      card.querySelector('[name="quantity"]').value = option.dataset.min || '1';
      updateProductImage(card.querySelector('.mb-cart-accessory-image'), option);
    }
  }

  onSubmit(event) {
    const form = event.target;
    if (!form.matches('[data-accessory-form], .mb-cart-layout, .mb-cart-popup-form')) return;
    if (this.busy || document.querySelector('[data-cart-unconfirmed]')) { event.preventDefault(); return; }
    if (!form.matches('[data-accessory-form]')) {
      if (this.checkoutPending) { event.preventDefault(); return; }
      const button = event.submitter;
      if (button?.name === 'checkout') {
        this.checkoutPending = button;
        button.setAttribute('data-checkout-pending', '');
        button.setAttribute('aria-busy', 'true');
        // Keep the submitter enabled so Shopify receives its native checkout field.
        this.checkoutTimer = setTimeout(() => this.resetCheckout(), 12000);
        queueMicrotask(() => { if (event.defaultPrevented) this.resetCheckout(); });
      }
      return; // Shopify owns the native cart submission and checkout redirect.
    }
    event.preventDefault();
    const fields = new FormData(form);
    const id = Number(fields.get('id'));
    if (!id) return;
    this.mutate(form.action, { items: [{ id, quantity: Number(fields.get('quantity') || 1) }] }, event.submitter, 'Item added to your cart.');
  }

  resetCheckout() {
    clearTimeout(this.checkoutTimer);
    this.checkoutPending?.removeAttribute('data-checkout-pending');
    this.checkoutPending?.removeAttribute('aria-busy');
    this.checkoutPending = null;
  }

  change(key, quantity, focus) {
    this.mutate(cartRoot().dataset.changeUrl, { id: key, quantity }, focus, quantity === 0 ? 'Item removed from your cart.' : 'Cart quantity updated.');
  }

  async renderSections(sections = {}) {
    const ids = this.sectionIDs();
    const missing = ids.filter((id) => typeof sections[id] !== 'string');
    const url = new URL(cartRoot().dataset.cartUrl, window.location.origin);
    for (let index = 0; index < missing.length; index += 5) {
      url.searchParams.set('sections', missing.slice(index, index + 5).join(','));
      Object.assign(sections, await requestJSON(url));
    }
    const note = document.querySelector('[data-cart-note]')?.value;
    const replacements = cartRegions().map((current) => {
      const html = sections[current.dataset.mbCartSection];
      if (!html) throw new Error('Your cart changed, but the page could not refresh. Please reload before continuing.');
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const replacement = [...parsed.querySelectorAll('[data-mb-cart-section]')].find((node) => node.dataset.mbCartSection === current.dataset.mbCartSection);
      if (!replacement) throw new Error('Please reload to see your latest cart.');
      return [current, replacement];
    });
    replacements.forEach(([current, replacement]) => {
      current.querySelectorAll('dialog[open]').forEach((dialog) => dialog.close());
      current.replaceWith(replacement);
    });
    this.enhance();
    const noteInput = document.querySelector('[data-cart-note]');
    if (note !== undefined && noteInput) noteInput.value = note;

    // Refresh only popup content, preserving its open state, focus trap and header.
    document.querySelectorAll('mb-cart-popup[data-section-id]').forEach((popup) => {
      const html = sections[popup.dataset.sectionId];
      const replacement = html && new DOMParser().parseFromString(html, 'text/html').querySelector('mb-cart-popup');
      if (!replacement) throw new Error('Please reload to see your latest cart preview.');
      popup.render(replacement);
    });
    // Original drawer mode still uses the theme's own header renderer.
    const drawerIDs = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map((node) => node.dataset.sectionId))];
    if (drawerIDs.length) {
      try {
        const { morphSection } = await import('@theme/section-renderer');
        for (const id of drawerIDs) if (sections[id]) await morphSection(id, sections[id]);
      } catch (error) {
        console.warn('Maya cart: the drawer will refresh on the next page load.', error);
      }
    }
    const itemCount = Number(cartRoot().dataset.itemCount);
    // Also update the theme's short-lived cart-count cache for subsequent navigation.
    document.querySelectorAll('cart-icon').forEach((icon) => icon.renderCartBubble?.(itemCount, false, false));
    document.dispatchEvent(new CustomEvent('maya:cart:updated', { detail: { itemCount } }));
  }

  async mutate(url, fields, focused, announcement) {
    if (this.busy) return;
    const region = focused?.closest('[data-mb-cart-section], mb-cart-popup');
    const popup = focused?.closest('mb-cart-popup');
    const focusKey = focused?.dataset.focusKey;
    const lineKey = focused?.closest('[data-line-key]')?.dataset.lineKey;
    const oldY = focused?.getBoundingClientRect().top;
    const oldScroll = window.scrollY;
    cartRegions().forEach((element) => this.error('', element));
    this.setBusy(true);
    try {
      const data = await requestJSON(jsonURL(url), {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...fields, sections: this.sectionIDs().slice(0, 5), sections_url: cartRoot().dataset.cartUrl }),
      });
      await this.renderSections(data.sections);
      const focusTarget = focusKey ? [...document.querySelectorAll('[data-focus-key]')].find((node) => node.dataset.focusKey === focusKey) : null;
      if (popup?.querySelector('dialog[open]')) {
        (focusTarget || popup.querySelector('h2'))?.focus({ preventScroll: true });
      } else if (focusTarget) {
        focusTarget.focus({ preventScroll: true });
        if (oldY !== undefined) window.scrollTo({ top: oldScroll + focusTarget.getBoundingClientRect().top - oldY, behavior: 'instant' });
      } else if (lineKey || focused?.matches('[data-accessory-form] button')) {
        cartRoot().querySelector('.mb-cart-title').focus({ preventScroll: true });
        if (Number(cartRoot().dataset.itemCount) === 0) cartRoot().scrollIntoView({ behavior: reduceMotion() ? 'instant' : 'smooth', block: 'start' });
      }
      cartRoot().querySelector('.mb-cart-status').textContent = announcement;
    } catch (error) {
      // A dropped response may still have changed the server cart. Read it back; never repeat an add.
      let recovered = true;
      try { await this.renderSections(); } catch (_) { recovered = false; }
      this.error(recovered ? (error.name === 'AbortError' || error instanceof TypeError ? 'The connection was interrupted. Your latest cart is shown; check it before trying again.' : error.message) : 'We could not confirm your latest cart. Please reload this page before continuing.', region);
      if (recovered && focusKey) [...document.querySelectorAll('[data-focus-key]')].find((node) => node.dataset.focusKey === focusKey)?.focus({ preventScroll: true });
      if (!recovered) document.querySelectorAll('.mb-cart-checkout').forEach((button) => button.setAttribute('data-cart-unconfirmed', ''));
    } finally {
      this.setBusy(false);
      document.querySelectorAll('[data-cart-unconfirmed]').forEach((button) => { button.disabled = true; });
      if (this.refreshAgain) { this.refreshAgain = false; this.refresh(); }
    }
  }

  async refresh() {
    if (!cartRoot()) return;
    if (this.busy) { this.refreshAgain = true; return; }
    this.setBusy(true);
    try { await this.renderSections(); }
    catch (_) { this.error('Please reload to see your latest cart.'); }
    finally { this.setBusy(false); }
  }

  async replaceVariant(key, variantID, focused) {
    if (this.busy) return;
    const originalScroll = window.scrollY;
    this.error('', cartRoot());
    this.setBusy(true);
    let failure;
    try {
      await replaceCartVariant({ request: requestJSON, cartURL: jsonURL(cartRoot().dataset.cartUrl), addURL: jsonURL(cartRoot().dataset.addUrl), changeURL: jsonURL(cartRoot().dataset.changeUrl), key, variantID });
    } catch (error) { failure = error; }
    try {
      await this.renderSections();
    } catch (_) {
      failure = Object.assign(new Error('Please reload to confirm the latest cart before continuing.'), { cartUnconfirmed: true });
    } finally {
      this.setBusy(false);
    }
    const row = [...document.querySelectorAll('.mb-cart-line')].find((item) => failure ? item.dataset.lineKey === key : item.dataset.variantId === String(variantID));
    const focusTarget = row?.querySelector('[data-cart-dialog-open]') || cartRoot()?.querySelector('.mb-cart-title');
    focusTarget?.focus({ preventScroll: true });
    window.scrollTo({ top: originalScroll, behavior: 'instant' });
    if (failure) {
      this.error(failure.message);
      const openDialog = cartRoot()?.querySelector('.mb-cart-configuration[open]');
      if (openDialog) {
        this.error(failure.message, openDialog);
        openDialog.querySelector('[data-cart-dialog-close]')?.focus({ preventScroll: true });
      }
      if (failure.cartUnconfirmed) {
        document.querySelectorAll('.mb-cart-checkout').forEach((checkout) => { checkout.disabled = true; checkout.setAttribute('data-cart-unconfirmed', ''); });
      }
    } else cartRoot().querySelector('.mb-cart-status').textContent = cartRoot().dataset.configUpdated;
    if (this.refreshAgain) { this.refreshAgain = false; this.refresh(); }
  }
}

class MayaCartDelivery extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    this.start = () => {
      clearInterval(this.timer);
      if (!document.hidden) {
        this.update();
        this.timer = setInterval(() => this.update(), 1000);
      }
    };
    document.addEventListener('visibilitychange', this.start, { signal: this.abort.signal });
    this.start();
  }
  disconnectedCallback() { clearInterval(this.timer); this.abort?.abort(); }
  update() {
    const timer = this.querySelector('[data-delivery-timer]');
    const date = this.querySelector('[data-delivery-date]');
    if (!timer || !date) return;
    const estimate = deliveryEstimate(new Date(), this.dataset.cutoffHour, this.dataset.deliveryDays);
    const timerText = `${estimate.hours} ${this.dataset.hoursLabel} ${estimate.minutes} ${this.dataset.minutesLabel}`;
    const dateText = estimate.arrival.toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
    if (timer.textContent !== timerText) timer.textContent = timerText;
    if (date.textContent !== dateText) date.textContent = dateText;
    this.querySelector('[data-delivery-fallback]').hidden = true;
    this.querySelector('[data-delivery-live]').hidden = false;
  }
}

class MayaCartConfiguration extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    this.variants = [...this.querySelector('[data-config-variants]').options].map((option) => ({ option, id: option.value, values: JSON.parse(option.dataset.options), available: option.dataset.available === 'true' }));
    this.fields = [...this.querySelectorAll('[data-config-option]')];
    this.addEventListener('change', () => this.update(), { signal: this.abort.signal });
    this.querySelector('form').addEventListener('submit', (event) => {
      event.preventDefault();
      if (!this.selected || this.querySelector('[data-config-save]').disabled) return;
      this.dispatchEvent(new CustomEvent('maya:cart:configuration', { bubbles: true, detail: { key: this.dataset.lineKey, variantID: this.selected.id, focus: event.submitter } }));
    }, { signal: this.abort.signal });
    this.reset();
  }
  disconnectedCallback() { this.abort?.abort(); }
  reset() {
    this.querySelector('form').reset();
    const error = this.querySelector('.mb-cart-error');
    error.textContent = '';
    error.hidden = true;
    this.update();
  }
  update() {
    const values = this.fields.map((field) => field.querySelector('input:checked')?.value);
    this.fields.forEach((field, index) => { const label = field.querySelector('[data-config-color-label]'); if (label) label.textContent = values[index] || ''; });
    this.selected = this.variants.find((variant) => variant.values.every((value, index) => value === values[index]));
    const option = this.selected?.option;
    const quantity = Number(this.dataset.quantity);
    const min = Number(option?.dataset.min || 1);
    const step = Number(option?.dataset.step || 1);
    const max = Number(option?.dataset.max || Infinity);
    const available = this.selected?.available && quantity >= min && quantity <= max && (quantity - min) % step === 0;
    const button = this.querySelector('[data-config-save]');
    button.disabled = !available || this.selected.id === this.dataset.currentVariant;
    button.textContent = this.dataset.saveLabel;
    this.querySelector('[data-config-availability]').textContent = available ? '' : this.dataset.unavailableLabel;
    const financing = this.querySelector('[data-config-financing]');
    if (financing) {
      const amount = Number(option?.dataset.priceCents);
      financing.hidden = !available || !Number.isSafeInteger(amount) || amount <= 0;
      if (!financing.hidden) updateAffirmAmount(financing, amount);
    }
    if (option) {
      this.querySelector('[data-config-price]').textContent = option.dataset.price;
      this.querySelector('[data-config-selected]').textContent = option.textContent;
      updateProductImage(this.querySelector('.mb-cart-config-image'), option, '(min-width: 768px) 400px, 280px');
    }
  }
}

class MayaCartBenefits extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const options = { signal: this.abort.signal };
    this.track = this.querySelector('.mb-cart-benefits-track');
    this.cards = [...this.track.children];
    this.cards.forEach((card, index) => { card.dataset.benefitIndex = index; });
    this.pages = [...this.querySelectorAll('[data-benefits-page]')];
    this.previous = this.querySelector('[data-benefits-prev]');
    this.next = this.querySelector('[data-benefits-next]');
    this.mobile = window.matchMedia('(max-width: 1023px)');
    this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.active = 0;
    this.elapsed = 0;
    this.lastScrollAt = 0;
    this.duration = Math.max(3, Number(this.dataset.duration) || 5) * 1000;
    this.setAttribute('data-enhanced', '');
    this.previous.addEventListener('click', () => this.move(-1), options);
    this.next.addEventListener('click', () => this.move(1), options);
    this.pages.forEach((button, index) => button.addEventListener('click', () => { this.elapsed = 0; this.goTo(index); }, options));
    this.track.addEventListener('scroll', () => {
      this.lastScrollAt = performance.now();
      this.updateControls();
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(() => this.settleLoop(), 180);
    }, { ...options, passive: true });
    this.track.addEventListener('keydown', (event) => {
      if (event.target !== this.track || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      if (event.key === 'Home' || event.key === 'End') this.goTo(event.key === 'Home' ? 0 : this.cards.length - 1);
      else this.move(event.key === 'ArrowRight' ? 1 : -1);
    }, options);
    this.mobile.addEventListener('change', () => this.configureLoop(), options);
    this.motion.addEventListener('change', () => { this.elapsed = 0; this.autoplayEnded = false; }, options);
    this.resizeObserver = new ResizeObserver(() => {
      const width = this.track.clientWidth;
      if (this.previousWidth !== width) { this.previousWidth = width; this.goTo(this.active, false, 'instant'); }
      this.updateControls();
    });
    this.resizeObserver.observe(this.track);
    this.intersectionObserver = new IntersectionObserver(([entry]) => { this.inView = entry.isIntersecting && entry.intersectionRatio >= .25; }, { threshold: [0, .25] });
    this.intersectionObserver.observe(this);
    this.addEventListener('click', (event) => this.onClick(event), options);
    this.querySelectorAll('dialog').forEach((dialog) => {
      dialog.addEventListener('close', () => this.onClose(), options);
      dialog.addEventListener('click', (event) => {
        if (event.target !== dialog) return;
        const rect = dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
      }, options);
    });
    this.track.addEventListener('pointerdown', (event) => { this.pointerActive = true; this.startDrag(event); }, options);
    this.track.addEventListener('pointermove', (event) => this.drag(event), options);
    window.addEventListener('pointerup', () => { this.pointerActive = false; this.endDrag(); }, options);
    this.track.addEventListener('pointercancel', () => { this.pointerActive = false; this.endDrag(); }, options);
    this.track.addEventListener('lostpointercapture', () => this.endDrag(), options);
    this.addEventListener('shopify:block:select', (event) => {
      const index = this.cards.findIndex((card) => card.dataset.benefitBlock === event.detail.blockId);
      if (index >= 0) this.goTo(index, false, 'instant');
    }, options);
    this.configureLoop();
    this.frame = requestAnimationFrame((now) => this.tick(now));
  }

  disconnectedCallback() {
    this.querySelectorAll('dialog[open]').forEach((dialog) => dialog.close());
    this.onClose();
    this.abort?.abort();
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    cancelAnimationFrame(this.frame);
    clearTimeout(this.settleTimer);
  }

  configureLoop() {
    this.track.querySelectorAll('[data-benefit-clone]').forEach((card) => card.remove());
    this.looped = this.mobile.matches && this.dataset.loop === 'true' && this.cards.length > 1 && this.dataset.designMode !== 'true';
    if (this.looped) {
      const clone = (card) => {
        const copy = card.cloneNode(true);
        copy.removeAttribute('data-benefit-block');
        copy.setAttribute('data-benefit-clone', '');
        copy.setAttribute('aria-hidden', 'true');
        copy.inert = true;
        [copy, ...copy.querySelectorAll('*')].forEach((node) => {
          node.removeAttribute('id');
          [...node.attributes].filter((attribute) => attribute.name.startsWith('data-shopify')).forEach((attribute) => node.removeAttribute(attribute.name));
        });
        return copy;
      };
      this.track.prepend(clone(this.cards[this.cards.length - 1]));
      this.track.append(clone(this.cards[0]));
    }
    this.goTo(this.active, false, 'instant');
    this.updateControls();
    this.autoplayEnded = false;
  }

  position(card) { return card.offsetLeft - this.track.firstElementChild.offsetLeft; }
  nearest() {
    return [...this.track.children].reduce((closest, card) => Math.abs(this.position(card) - this.track.scrollLeft) < Math.abs(this.position(closest) - this.track.scrollLeft) ? card : closest, this.track.firstElementChild);
  }
  settleLoop() {
    const nearest = this.nearest();
    if (this.looped && nearest?.hasAttribute('data-benefit-clone')) this.goTo(Number(nearest.dataset.benefitIndex), false, 'instant');
  }
  updateControls() {
    const max = this.track.scrollWidth - this.track.clientWidth;
    this.previous.disabled = !this.looped && this.track.scrollLeft < 2;
    this.next.disabled = !this.looped && this.track.scrollLeft >= max - 2;
    this.toggleAttribute('data-no-overflow', this.cards.length <= 1 || max < 2);
    const index = Number(this.nearest()?.dataset.benefitIndex || 0);
    if (index !== this.active) { this.active = index; this.elapsed = 0; this.autoplayEnded = false; }
    this.pages.forEach((button, page) => button.setAttribute('aria-current', String(page === this.active)));
  }
  goTo(index, announce = true, behavior) {
    const card = this.cards[index];
    if (!card) return;
    this.track.scrollTo({ left: this.position(card), behavior: behavior || (reduceMotion() ? 'instant' : 'smooth') });
    if (announce) this.announce(index);
  }
  announce(index) {
    this.querySelector('[data-benefits-status]').textContent = this.pages[index]?.getAttribute('aria-label') || '';
  }
  move(direction, announce = true) {
    if (!this.cards.length) return;
    if (this.mobile.matches) {
      const index = this.active + direction;
      if (this.looped && (index < 0 || index >= this.cards.length)) {
        const clone = direction > 0 ? this.track.lastElementChild : this.track.firstElementChild;
        this.track.scrollTo({ left: this.position(clone), behavior: reduceMotion() ? 'instant' : 'smooth' });
        if (announce) this.announce(Number(clone.dataset.benefitIndex));
      } else this.goTo(Math.max(0, Math.min(this.cards.length - 1, index)), announce);
    } else {
      const step = this.cards[0].getBoundingClientRect().width + parseFloat(getComputedStyle(this.track).gap);
      const max = this.track.scrollWidth - this.track.clientWidth;
      const current = this.track.scrollLeft;
      const target = Math.max(0, Math.min(max, (direction > 0 ? Math.floor((current + 2) / step) + 1 : Math.ceil((current - 2) / step) - 1) * step));
      this.track.scrollTo({ left: target, behavior: reduceMotion() ? 'instant' : 'smooth' });
      if (announce) this.announce(Math.round(target / step));
    }
  }
  tick(now) {
    const delta = Math.min(100, this.previousTime ? now - this.previousTime : 0);
    this.previousTime = now;
    const autoplay = this.dataset.autoplay === 'true' && this.mobile.matches && !this.motion.matches && this.dataset.designMode !== 'true' && this.cards.length > 1;
    const focused = document.activeElement;
    const keyboardFocus = document.documentElement.dataset.mbCartInput !== 'pointer' && this.contains(focused) && focused.matches(':focus-visible');
    const canRun = autoplay && !this.autoplayEnded && this.inView && !document.hidden && !this.pointerActive && !keyboardFocus && !document.querySelector('.mb-cart-dialog[open], .mb-cart-popup-dialog[open]') && now - this.lastScrollAt > 200;
    if (canRun) {
      this.elapsed += delta;
      if (this.elapsed >= this.duration) {
        if (!this.looped && this.active === this.cards.length - 1) { this.elapsed = this.duration; this.autoplayEnded = true; }
        else { this.elapsed = 0; this.move(1, false); }
      }
    }
    this.pages.forEach((button, index) => {
      const progress = index === this.active ? (autoplay ? this.elapsed / this.duration : 1) : 0;
      button.querySelector('.mb-cart-progress-fill').style.transform = `scaleX(${progress})`;
    });
    this.frame = requestAnimationFrame((time) => this.tick(time));
  }
  startDrag(event) {
    if (event.pointerType !== 'mouse' || event.button !== 0 || event.target.closest('a, summary, button:not([data-benefit-open])')) return;
    this.suppressCardClick = false;
    this.dragState = { x: event.clientX, left: this.track.scrollLeft, id: event.pointerId, moved: false };
  }
  drag(event) {
    const state = this.dragState;
    if (!state) return;
    if (event.buttons !== 1) { this.endDrag(); return; }
    const distance = state.x - event.clientX;
    if (!state.moved && Math.abs(distance) < 5) return;
    if (!state.moved) { state.moved = true; this.track.setPointerCapture(state.id); this.track.setAttribute('data-dragging', ''); }
    this.track.scrollLeft = state.left + distance;
  }
  endDrag() {
    const state = this.dragState;
    if (!state) return;
    this.suppressCardClick = state.moved;
    this.dragState = null;
    this.track.removeAttribute('data-dragging');
    if (this.track.hasPointerCapture(state.id)) this.track.releasePointerCapture(state.id);
  }
  onClick(event) {
    const opener = event.target.closest('[data-benefit-open]');
    if (opener) {
      if (this.suppressCardClick && event.detail > 0) { this.suppressCardClick = false; event.preventDefault(); return; }
      const dialog = document.getElementById(opener.dataset.benefitOpen);
      if (!dialog) return;
      this.opener = opener;
      document.body.classList.add('mb-cart-modal-open');
      dialog.showModal();
    }
    if (event.target.closest('[data-benefit-close]')) event.target.closest('dialog').close();
  }
  onClose() {
    if (!document.querySelector('.mb-cart-dialog[open]')) document.body.classList.remove('mb-cart-modal-open');
    if (this.opener?.isConnected) this.opener.focus({ preventScroll: true });
  }
}

if (!customElements.get('mb-cart-benefits')) customElements.define('mb-cart-benefits', MayaCartBenefits);
if (!customElements.get('mb-cart-configuration')) customElements.define('mb-cart-configuration', MayaCartConfiguration);
if (!customElements.get('mb-cart-delivery')) customElements.define('mb-cart-delivery', MayaCartDelivery);
new MayaCartController();
